(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const formatMoney = (cents, format = window.theme?.moneyFormat || '${{amount}}') => {
    const value = Number(cents || 0);
    const amount = (value / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  };

  const bodyClass = (name, active) => document.body.classList.toggle(name, active);

  function setupPanel(openers, panel, overlay, bodyClassName) {
    if (!panel) return;
    const close = () => {
      panel.classList.remove('is-open');
      overlay?.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      bodyClass(bodyClassName, false);
      openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    };
    const open = () => {
      panel.classList.add('is-open');
      overlay?.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      bodyClass(bodyClassName, true);
      openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      setTimeout(() => qs('button, a, input, select, textarea', panel)?.focus(), 40);
    };
    openers.forEach((button) => button.addEventListener('click', open));
    qsa('[data-close]', panel).forEach((button) => button.addEventListener('click', close));
    overlay?.addEventListener('click', close);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel.classList.contains('is-open')) close();
    });
    return { open, close };
  }

  const navPanel = setupPanel(
    qsa('[data-menu-open]'),
    qs('[data-menu-drawer]'),
    qs('[data-menu-overlay]'),
    'drawer-open'
  );

  qsa('[data-submenu-toggle]').forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      submenu?.classList.toggle('is-open', !open);
    });
  });

  const searchPanel = setupPanel(
    qsa('[data-search-open]'),
    qs('[data-search-panel]'),
    null,
    'search-open'
  );

  let cartPanel;

  async function fetchCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  function renderCart(cart) {
    qsa('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    const lines = qs('[data-cart-lines]');
    const empty = qs('[data-cart-empty]');
    const summary = qs('[data-cart-summary]');
    if (!lines) return;
    if (!cart.items.length) {
      lines.innerHTML = '';
      empty?.removeAttribute('hidden');
      summary?.setAttribute('hidden', '');
      return;
    }
    empty?.setAttribute('hidden', '');
    summary?.removeAttribute('hidden');
    lines.innerHTML = cart.items.map((item, index) => `
      <article class="dd-cart-line" data-cart-line data-line="${index + 1}">
        <a href="${item.url}">
          ${item.image ? `<img src="${item.image}" alt="${(item.product_title || '').replace(/"/g, '&quot;')}" width="168" height="208" loading="lazy">` : ''}
        </a>
        <div>
          <h3 class="dd-cart-line__title"><a href="${item.url}">${item.product_title}</a></h3>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="dd-cart-line__variant">${item.variant_title}</div>` : ''}
          <div class="dd-quantity">
            <button type="button" aria-label="Decrease quantity" data-cart-minus>−</button>
            <input type="number" min="0" value="${item.quantity}" aria-label="Quantity" data-cart-quantity>
            <button type="button" aria-label="Increase quantity" data-cart-plus>+</button>
          </div>
        </div>
        <div class="dd-cart-line__price">${formatMoney(item.final_line_price)}</div>
      </article>
    `).join('');
    const subtotal = qs('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
  }

  async function refreshCart(open = false) {
    try {
      const cart = await fetchCart();
      renderCart(cart);
      if (open) cartPanel?.open();
    } catch (error) {
      console.error(error);
    }
  }

  async function changeLine(line, quantity) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Unable to update cart');
    renderCart(await response.json());
  }

  cartPanel = setupPanel(
    qsa('[data-cart-open]'),
    qs('[data-cart-drawer]'),
    qs('[data-cart-overlay]'),
    'drawer-open'
  );

  document.addEventListener('click', (event) => {
    const line = event.target.closest('[data-cart-line]');
    if (!line) return;
    const input = qs('[data-cart-quantity]', line);
    if (event.target.closest('[data-cart-minus]')) {
      const quantity = Math.max(0, Number(input.value) - 1);
      input.value = quantity;
      changeLine(Number(line.dataset.line), quantity).catch(console.error);
    }
    if (event.target.closest('[data-cart-plus]')) {
      const quantity = Number(input.value) + 1;
      input.value = quantity;
      changeLine(Number(line.dataset.line), quantity).catch(console.error);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    const line = input.closest('[data-cart-line]');
    changeLine(Number(line.dataset.line), Math.max(0, Number(input.value))).catch(console.error);
  });

  qsa('form[action*="/cart/add"]').forEach((form) => {
    if (form.dataset.noAjax === 'true') return;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submit = qs('[type="submit"]', form);
      const original = submit?.textContent;
      if (submit) { submit.disabled = true; submit.textContent = window.theme?.addingText || 'Adding…'; }
      const status = qs('[data-product-status]', form.closest('[data-product-root]') || form);
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Unable to add this item');
        if (status) status.textContent = window.theme?.addedText || 'Added to your cart.';
        await refreshCart(true);
      } catch (error) {
        if (status) status.textContent = error.message;
      } finally {
        if (submit) { submit.disabled = false; submit.textContent = original; }
      }
    });
  });

  qsa('[data-product-root]').forEach((root) => {
    const json = qs('[data-product-json]', root);
    const select = qs('[data-variant-select]', root);
    if (!json || !select) return;
    let product;
    try { product = JSON.parse(json.textContent); } catch { return; }
    const price = qs('[data-product-price]', root);
    const compare = qs('[data-compare-price]', root);
    const submit = qs('[data-add-to-cart]', root);
    const availability = qs('[data-availability]', root);
    select.addEventListener('change', () => {
      const variant = product.variants.find((item) => String(item.id) === select.value);
      if (!variant) return;
      if (price) price.textContent = formatMoney(variant.price);
      if (compare) {
        compare.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !(variant.compare_at_price > variant.price);
      }
      if (submit) {
        submit.disabled = !variant.available;
        submit.textContent = variant.available ? (window.theme?.addToCartText || 'Add to cart') : (window.theme?.soldOutText || 'Sold out');
      }
      if (availability) {
        availability.textContent = variant.available ? (window.theme?.inStockText || 'In stock') : (window.theme?.soldOutText || 'Sold out');
        availability.classList.toggle('is-available', variant.available);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    });
  });

  qsa('[data-quantity-root]').forEach((root) => {
    const input = qs('input', root);
    qs('[data-quantity-minus]', root)?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value) - 1); });
    qs('[data-quantity-plus]', root)?.addEventListener('click', () => { input.value = Number(input.value) + 1; });
  });

  qsa('[data-sort-by]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
    });
  });

  qsa('[data-predictive-search-input]').forEach((input) => {
    let timer;
    const results = qs('[data-predictive-results]', input.closest('[data-search-panel]'));
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (!results || query.length < 2) { if (results) results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        const url = `${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        try {
          const response = await fetch(url);
          results.innerHTML = await response.text();
        } catch (error) { console.error(error); }
      }, 180);
    });
  });

  refreshCart(false);
  window.DiamondDream = { refreshCart, navPanel, searchPanel, cartPanel };
})();

(() => {
  const modals = [...document.querySelectorAll('[data-dd-modal]')];
  if (!modals.length) return;

  let activeModal = null;
  let lastTrigger = null;

  const closeModal = (modal = activeModal) => {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('modal-open');
    activeModal = null;
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
  };

  const openModal = (modal, trigger = null) => {
    if (!modal) return;
    if (activeModal && activeModal !== modal) closeModal(activeModal);
    lastTrigger = trigger || document.activeElement;
    activeModal = modal;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open');
    window.setTimeout(() => {
      const success = modal.querySelector('[data-modal-success]');
      const focusTarget = success || modal.querySelector('input, select, textarea, button');
      focusTarget?.focus();
    }, 40);
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      event.preventDefault();
      openModal(document.getElementById(opener.dataset.modalOpen), opener);
      return;
    }
    const closer = event.target.closest('[data-modal-close]');
    if (closer) {
      event.preventDefault();
      closeModal(closer.closest('[data-dd-modal]'));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeModal) closeModal(activeModal);
  });

  modals.forEach((modal) => {
    if (modal.querySelector('[data-modal-success]') || modal.querySelector('.dd-form-errors')) openModal(modal);
  });
})();
